import { useEffect, useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import './datepicker.css';
import { Presets } from './types';
import type { Placement } from '@floating-ui/react';
import ptBR from 'date-fns/locale/pt-BR';

import DateUtils from '@cobliteam/base-components/src/DateUtils';

import DatePickerDay from './components/DatePickerDay';
import DatePickerHeader from './components/DatePickerHeader';
import DatePickerPresets from './components/DatePickerPresets';
import DatePickerTrigger from './components/DatePickerTrigger';

interface DatePickerProps {
  startDate?: Date | null;
  endDate?: Date | null;
  minDate?: Date;
  maxDate?: Date;
  maxDays?: number;
  presets?: Presets[];
  popperPlacement?: Placement;
  onChange: (range: [Date, Date]) => void;
  onCalendarOpen?: () => void;
  onSelect?: (date: Date) => void;
}

const DatePicker = ({
  startDate = new Date(),
  endDate = new Date(),
  minDate,
  maxDate,
  maxDays,
  presets,
  popperPlacement = 'bottom-start',
  onChange,
  onCalendarOpen,
  onSelect,
}: DatePickerProps) => {
  registerLocale('ptBR', ptBR);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    startDate,
    endDate,
  ]);
  const [dateHover, setDateHover] = useState<Date | null>(null);
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<Presets | null>(Presets.TODAY);
  const [maxEndDate, setMaxEndDate] = useState<Date | undefined>(maxDate);

  const [startRangeDate, endRangeDate] = dateRange;

  const handleDateChange = (range: [Date | null, Date | null]) => {
    setDateRange(range);

    if (range[0] && range[1]) onChange(range as [Date, Date]);

    const end = range[1];
    if (end) setOpen(false);
  };

  const handleDateSelect = (date: Date) => {
    if (!maxDays) return;

    const newEndDate = DateUtils.addDays(date, maxDays);
    const newMaxEndDate = DateUtils.isBeforeDate(newEndDate, maxEndDate)
      ? newEndDate
      : maxEndDate;

    setMaxEndDate(newMaxEndDate);
  };

  useEffect(() => {
    if (!open && endRangeDate) setMaxEndDate(maxDate);
  }, [open, endRangeDate, maxDate]);

  return (
    <ReactDatePicker
      // Base props
      locale='ptBR'
      portalId='datepicker-portal'
      dateFormat='dd/MM/yyyy'
      // Date props
      open={open}
      selected={startRangeDate}
      startDate={startRangeDate}
      endDate={endRangeDate}
      selectsStart
      selectsRange
      monthsShown={2}
      showPopperArrow={false}
      popperPlacement={popperPlacement}
      minDate={minDate}
      maxDate={maxEndDate}
      focusSelectedMonth
      // Methods
      onDayMouseEnter={setDateHover}
      onCalendarOpen={onCalendarOpen}
      onChange={handleDateChange}
      onSelect={onSelect || handleDateSelect}
      onChangeRaw={() => setPreset(null)}
      onInputClick={() => setOpen(true)}
      onClickOutside={() => setOpen(false)}
      // Custom elements
      formatWeekDay={(nameOfDay) => (
        <div className='font-bold text-gray'>
          {nameOfDay.toUpperCase().at(0)}
        </div>
      )}
      renderCustomHeader={DatePickerHeader}
      renderDayContents={(day, date) => (
        <DatePickerDay
          day={day}
          date={date}
          startDate={startRangeDate}
          endDate={endRangeDate}
          dateHover={dateHover}
        />
      )}
      customInput={<DatePickerTrigger open={open} />}
    >
      <DatePickerPresets
        preset={preset}
        presets={presets}
        onChangePreset={setPreset}
        onChange={handleDateChange}
      />
    </ReactDatePicker>
  );
};

export default DatePicker;
